import { Component, OnInit, Injector, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { procedureI } from 'app/core/interfaces/procedure';
import { ProcedureService } from 'app/core/http/services/procedure.service';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { telecom } from 'app/core/models/Organization';
import { Address } from 'cluster';
import { OrganizationLocationService } from 'app/core/http/services/organization-location.service';
import { HttpParams } from '@angular/common/http';
import { Healthcareservice } from 'app/core/interfaces/healthcareservice';
import { OrganizationService } from 'app/modules/organization/organization.service';
import { OrganizationInfoDialogComponent } from 'app/modules/shared/components/organization-info-dialog/organization-info-dialog.component';
import { ScheduleSpecialistByOrganizationComponent } from 'app/modules/shared/pages/schedule-specialist-by-organization/schedule-specialist-by-organization.component';
import { ScheduleOrganizationCardComponent } from 'app/modules/shared/components/schedule-organization-card/schedule-organization-card.component';
import {
  addScheduleSearchSuggestion,
  friendlyProcedureName,
  groupScheduleSearchSuggestions,
  normalizeScheduleSearch,
  ScheduleSearchMode,
  ScheduleSearchSuggestion,
  ScheduleSearchSuggestionGroup,
} from 'app/modules/shared/models/schedule-search.models';
import { summarizeSchedulePayment } from 'app/modules/shared/models/schedule-payment-summary';

interface ProcedureCatalogGroup {
  key: string;
  name: string;
  services: Healthcareservice[];
  clinicCount: number;
}

interface ClinicCatalogGroup {
  key: string;
  organizationId: string;
  name: string;
  services: Healthcareservice[];
}

@Component({
  selector: 'page-schedule-procedure',
  templateUrl: './schedule-procedure.component.html',
  styleUrls: ['./schedule-procedure.component.scss'],
})
export class ScheduleProcedurePatientComponent extends BaseComponent implements OnInit {
  public title = 'Agendar';
  public searchControl = new FormControl('');
  public procedures: Healthcareservice[] = [];
  public specialistResults: any[] = [];
  public proceduresHLFIR: any[] = [];
  public address: Address[] = [];
  public locations: any[] = [];
  public organizations: any[] = [];
  public patientLogged: any = {};
  public latLong: any[] = [];
  public nextLatLong: [number, number] = [0, 0];
  public currentPage: number = 0;
  public pageSize: number = 10;
  public distance: number = 200000; // valor padrão do raio de distância

  public autocompleteSuggestions: ScheduleSearchSuggestion[] = [];
  public showAutocompletePanel: boolean = false;
  public showAutocompleteHint: boolean = false;
  public activeSuggestionIndex: number = -1;
  public activeSearchMode: ScheduleSearchMode = 'procedure';
  public expandedGroupKey: string | null = null;

  public hasSearched = false;

  public get search(): string {
    return this.searchControl.value || '';
  }

  public get showMessage(): boolean {
    if (this.loading || this.latLong.length == 0 || (this.procedures.length == 0 && this.latLong.length != 0)) {
      return true;
    }
    return false;
  }

  public get message(): string {
    return '';
  }

  public get proceduresFilter(): any[] {
    const searchTerm = normalizeScheduleSearch(this.search);
    return this.procedures.filter((item: any) => {
      if (!searchTerm) return true;

      const organizationId = item.organization?.organization_id;
      const providerNames = this.getSpecialistsForProcedure(item)
        .map((provider) => provider.practitioner_name)
        .join(' ');
      const searchableValues = [
        friendlyProcedureName(item.name),
        item.type,
        item.specialty,
        item.organization?.name,
        this.getCity(organizationId),
        item.location?.address?.city,
        providerNames,
      ];

      return searchableValues.some((value) => normalizeScheduleSearch(value).includes(searchTerm));
    });
  }

  public get procedureGroups(): ProcedureCatalogGroup[] {
    const groups = new Map<string, ProcedureCatalogGroup>();

    this.proceduresFilter.forEach((procedure: Healthcareservice) => {
      const normalizedName = normalizeScheduleSearch(procedure.name);
      const key = procedure.tuss_code ? `tuss:${procedure.tuss_code}` : `name:${normalizedName}`;
      const group = groups.get(key) ?? {
        key,
        name: friendlyProcedureName(procedure.name),
        services: [],
        clinicCount: 0,
      };
      const existingService = group.services.some(
        (service) => service.healthcareservice_id === procedure.healthcareservice_id && this.getClinicKey(service) === this.getClinicKey(procedure),
      );

      if (!existingService) group.services.push(procedure);
      groups.set(key, group);
    });

    return Array.from(groups.values()).map((group) => ({
      ...group,
      clinicCount: new Set(group.services.map((service) => this.getClinicKey(service))).size,
    }));
  }

  public get clinicGroups(): ClinicCatalogGroup[] {
    const groups = new Map<string, ClinicCatalogGroup>();

    this.proceduresFilter.forEach((procedure: Healthcareservice) => {
      const organizationId = procedure.organization.organization_id;
      const clinicKey = this.getClinicKey(procedure);
      const group = groups.get(clinicKey) ?? {
        key: clinicKey,
        organizationId,
        name: procedure.organization.name,
        services: [],
      };
      if (
        !group.services.some((service) => service.healthcareservice_id === procedure.healthcareservice_id && this.getClinicKey(service) === clinicKey)
      ) {
        group.services.push(procedure);
      }
      groups.set(clinicKey, group);
    });

    return Array.from(groups.values());
  }

  public get paginatedProcedureGroups(): ProcedureCatalogGroup[] {
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    return this.procedureGroups.slice(start, end);
  }

  public get paginatedClinicGroups(): ClinicCatalogGroup[] {
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    return this.clinicGroups.slice(start, end);
  }

  public get totalPages(): number {
    const totalGroups = this.activeSearchMode === 'clinic' ? this.clinicGroups.length : this.procedureGroups.length;
    return Math.ceil(totalGroups / this.pageSize);
  }

  public get catalogGroupCount(): number {
    return this.activeSearchMode === 'clinic' ? this.clinicGroups.length : this.procedureGroups.length;
  }

  public get autocompleteSuggestionGroups(): ScheduleSearchSuggestionGroup[] {
    return groupScheduleSearchSuggestions(this.autocompleteSuggestions);
  }

  public get visibleAutocompleteSuggestions(): ScheduleSearchSuggestion[] {
    return this.autocompleteSuggestionGroups.reduce((suggestions, group) => suggestions.concat(group.suggestions), [] as ScheduleSearchSuggestion[]);
  }

  public get activeSuggestionId(): string | null {
    return this.activeSuggestionIndex >= 0 ? `schedule-search-option-${this.activeSuggestionIndex}` : null;
  }

  constructor(
    private organizationService: OrganizationService,
    private procedureService: ProcedureService,
    private locationService: OrganizationLocationService,
    private injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    const userLogged = this.getUserLogged();
    this.loading = true;

    this.patientLogged = {
      patient_id: userLogged.id,
      name: userLogged.name,
      email: userLogged?.email,
    };

    this.activeSearchMode = this.router.url.includes('/agendar/clinicas') ? 'clinic' : 'procedure';
    this.searchControl.setValue(localStorage.getItem('searchControl'));

    this.getOrganizations();

    this.latLong.length === 0 ? this.getCurrentPosition() : this.getProceduresNearest();

    this.setPatientEncounterRaio();
    this.getLocations();
    this.setupAutocomplete();
  }

  setupAutocomplete() {
    this.searchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((searchTerm: any) => {
      this.updateAutocompleteSuggestions(searchTerm);
      this.currentPage = 0;
    });
  }

  updateAutocompleteSuggestions(searchTerm: string | null) {
    const term = searchTerm ?? '';
    localStorage.setItem('searchControl', term.toLowerCase());
    const trimmedTerm = term.trim();
    this.activeSuggestionIndex = -1;

    if (trimmedTerm.length < 2) {
      this.autocompleteSuggestions = [];
      this.showAutocompletePanel = false;
      this.showAutocompleteHint = trimmedTerm.length > 0;
      return;
    }

    const suggestions: ScheduleSearchSuggestion[] = [];
    this.procedures.forEach((procedure) => {
      addScheduleSearchSuggestion(
        suggestions,
        {
          label: friendlyProcedureName(procedure.name),
          category: 'Exames e procedimentos',
          mode: 'procedure',
        },
        trimmedTerm,
      );

      addScheduleSearchSuggestion(
        suggestions,
        {
          label: procedure.organization?.name,
          category: 'Clínicas e locais',
          mode: 'clinic',
        },
        trimmedTerm,
      );

      const city = this.getCity(procedure.organization.organization_id);
      if (city) {
        addScheduleSearchSuggestion(
          suggestions,
          {
            label: city,
            category: 'Clínicas e locais',
            mode: 'clinic',
          },
          trimmedTerm,
        );
      }
    });

    this.specialistResults.forEach((result) =>
      addScheduleSearchSuggestion(
        suggestions,
        {
          label: result.practitioner_name,
          category: 'Especialistas',
          mode: 'specialist',
        },
        trimmedTerm,
      ),
    );

    this.autocompleteSuggestions = suggestions;
    this.showAutocompletePanel = true;
    this.showAutocompleteHint = false;
  }

  getSuggestionId(suggestion: ScheduleSearchSuggestion): string {
    return `schedule-search-option-${this.visibleAutocompleteSuggestions.indexOf(suggestion)}`;
  }

  isSuggestionActive(suggestion: ScheduleSearchSuggestion): boolean {
    return this.visibleAutocompleteSuggestions.indexOf(suggestion) === this.activeSuggestionIndex;
  }

  activateSuggestion(suggestion: ScheduleSearchSuggestion): void {
    this.activeSuggestionIndex = this.visibleAutocompleteSuggestions.indexOf(suggestion);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.search.trim().length >= 2 && !this.showAutocompletePanel) {
        this.updateAutocompleteSuggestions(this.search);
      }

      const suggestions = this.visibleAutocompleteSuggestions;
      if (suggestions.length === 0) return;

      event.preventDefault();
      this.showAutocompletePanel = true;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeSuggestionIndex =
        this.activeSuggestionIndex < 0
          ? direction > 0
            ? 0
            : suggestions.length - 1
          : (this.activeSuggestionIndex + direction + suggestions.length) % suggestions.length;
      return;
    }

    if (event.key === 'Enter' && this.showAutocompletePanel) {
      event.preventDefault();
      const suggestion = this.visibleAutocompleteSuggestions[this.activeSuggestionIndex];
      if (suggestion) {
        this.selectSuggestion(suggestion);
      } else {
        this.showAutocompletePanel = false;
      }
      return;
    }

    if (event.key === 'Escape' && this.showAutocompletePanel) {
      event.preventDefault();
      this.showAutocompletePanel = false;
      this.activeSuggestionIndex = -1;
    }
  }

  selectSuggestion(suggestion: ScheduleSearchSuggestion) {
    localStorage.setItem('searchControl', suggestion.label.toLowerCase());
    this.searchControl.setValue(suggestion.label, { emitEvent: false });
    this.showAutocompletePanel = false;
    this.activeSuggestionIndex = -1;
    this.navigateToMode(suggestion.mode);
  }

  navigateToMode(mode: ScheduleSearchMode) {
    localStorage.setItem('searchControl', this.search.toLowerCase());
    this.activeSearchMode = mode;
    this.expandedGroupKey = null;
    this.currentPage = 0;
    const routes: Record<ScheduleSearchMode, string> = {
      procedure: '/patient-new/agendar/exame',
      specialist: '/patient-new/agendar/especialistas/exame',
      clinic: '/patient-new/agendar/clinicas',
    };
    this.router.navigate([routes[mode]]);
  }

  toggleGroup(key: string) {
    this.expandedGroupKey = this.expandedGroupKey === key ? null : key;
  }

  getSpecialistsForProcedure(procedure: Healthcareservice): any[] {
    const providers = this.specialistResults.filter(
      (item) => item.healthcareservice_id === procedure.healthcareservice_id && item.organization_id === procedure.organization.organization_id,
    );
    const uniqueProviders = new Map<string, any>();
    providers.forEach((provider) => uniqueProviders.set(provider.practitioner_id, provider));
    return Array.from(uniqueProviders.values());
  }

  getSpecialistNamesForProcedure(procedure: Healthcareservice): string[] {
    return this.getSpecialistsForProcedure(procedure)
      .map((provider) => provider.practitioner_name)
      .filter(Boolean);
  }

  getClinicDisplayName(procedure: Healthcareservice): string {
    const organizationId = procedure.organization.organization_id;
    const serviceLocation = procedure.location?.address;
    const organizationLocation = this.getAddress(organizationId);
    const cityName = serviceLocation?.city || organizationLocation?.city;
    const state = serviceLocation?.state || organizationLocation?.state;
    const city = cityName ? `${cityName}${state ? `, ${state}` : ''}` : null;
    return city ? `${procedure.organization.name} - ${city}` : procedure.organization.name;
  }

  getClinicKey(procedure: Healthcareservice): string {
    const organizationId = procedure.organization.organization_id;
    const location = procedure.location;
    if (location?.location_id) return `clinic:${organizationId}:${location.location_id}`;

    const address = location?.address;
    const organizationAddress = this.getAddress(organizationId);
    const addressSignature = [
      address?.street || organizationAddress?.street,
      address?.number || organizationAddress?.number,
      address?.city || organizationAddress?.city,
      address?.state || organizationAddress?.state,
    ]
      .map((value) => normalizeScheduleSearch(value))
      .filter(Boolean)
      .join('|');

    return addressSignature ? `clinic:${organizationId}:${addressSignature}` : `clinic:${organizationId}`;
  }

  getProcedureDisplayName(name: string): string {
    return friendlyProcedureName(name);
  }

  getClinicLocationLabel(procedure: Healthcareservice): string {
    const serviceLocation = procedure.location?.address;
    const organizationLocation = this.getAddress(procedure.organization.organization_id);
    const city = serviceLocation?.city || organizationLocation?.city;
    const state = serviceLocation?.state || organizationLocation?.state;
    const cityLabel = city ? `${city}${state ? `, ${state}` : ''}` : '';
    const locationName =
      normalizeScheduleSearch(procedure.location?.name) === normalizeScheduleSearch(procedure.organization.name) ? '' : procedure.location?.name;
    return [locationName, cityLabel].filter(Boolean).join(' · ') || 'Localização não informada';
  }

  getProcedurePriceLabel(procedure: Healthcareservice): string {
    return summarizeSchedulePayment([procedure]).privatePriceLabel;
  }

  getProcedureCoverageLabel(procedure: Healthcareservice): string {
    return summarizeSchedulePayment([procedure]).coverageLabel;
  }

  getProcedureGroupPriceLabel(group: ProcedureCatalogGroup): string {
    return summarizeSchedulePayment(group.services).privatePriceLabel;
  }

  getProcedureGroupCoverageLabel(group: ProcedureCatalogGroup): string {
    return summarizeSchedulePayment(group.services).coverageLabel;
  }

  getClinicPriceLabel(group: ClinicCatalogGroup): string {
    return summarizeSchedulePayment(group.services).privatePriceLabel;
  }

  getClinicCoverageLabel(group: ClinicCatalogGroup): string {
    return summarizeSchedulePayment(group.services).coverageLabel;
  }

  onInputFocus() {
    if (this.search.trim().length >= 2) {
      this.updateAutocompleteSuggestions(this.search);
    } else {
      this.showAutocompleteHint = this.search.trim().length > 0;
    }
  }

  onInputBlur() {
    setTimeout(() => {
      this.showAutocompletePanel = false;
      this.activeSuggestionIndex = -1;
    }, 150);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const inputElement = document.querySelector('input[matInput]');
    const panelElement = document.querySelector('.autocomplete-panel');

    if (inputElement && panelElement) {
      const clickedInsideInput = inputElement.contains(event.target as Node);
      const clickedInsidePanel = panelElement.contains(event.target as Node);

      if (!clickedInsideInput && !clickedInsidePanel) {
        this.showAutocompletePanel = false;
        this.activeSuggestionIndex = -1;
      }
    }
  }

  hasButton() {
    if (this.latLong.length == 0) {
      return 'col-xs-12 col-lg-8 margin';
    }
    return 'col-xs-12 ';
  }

  setPatientEncounterRaio() {
    const distanceInKm = localStorage.getItem('distancePatient') ?? '20000';

    this.distance = Number(distanceInKm);
  }

  public getCurrentPosition(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.latLong = [position.coords.latitude, position.coords.longitude];

          this.getProceduresNearest();
          this.getLocations();
        },
        (error: GeolocationPositionError) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              console.error('User denied the request for Geolocation.');
              break;
            case error.POSITION_UNAVAILABLE:
              console.error('Location information is unavailable.');
              break;
            case error.TIMEOUT:
              console.error('The request to get user location timed out.');
              break;
            default:
              console.error('An unknown error occurred.');
              break;
          }
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
    }
  }

  async openEncounterRegistrationForm(procedure: any) {
    await this.getOrganizationLocation(procedure);

    const organization = this.organizations.find((organization) => organization.organization_id == procedure?.organization.organization_id);

    const data = {
      title: 'Agendar consulta',
      param: {
        procedure: procedure,
        type: 'Exame',
        actor: 'patient',
        address: this.getAddressForProcedure(procedure),
        patient: this.patientLogged,
        latLong: this.nextLatLong,
        distance: this.distance,
        position: this.latLong.join(','),
        organization: organization,
        organization_id: procedure?.organization.organization_id,
        healthcareservice_id: procedure?.healthcareservice_id,
        dealer: procedure?.serviceDealer,
      },
    };

    if (procedure?.serviceDealer == 'Organização') {
      this.dialog.openComponentDialog(ScheduleOrganizationCardComponent, data).subscribe((res: any) => {
        window.location.reload();
      });
    } else {
      this.dialog.openComponentDialog(ScheduleSpecialistByOrganizationComponent, data).subscribe((res: any) => {
        window.location.reload();
      });
    }
  }

  getOrganizationLocation(procedure: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const organizationId = procedure.organization.organization_id;

      this.locationService.showByOrganization(organizationId).subscribe({
        next: (resOrganizationLocation) => {
          this.nextLatLong = [resOrganizationLocation.position.latitude ?? 0, resOrganizationLocation.position.longitude ?? 0];
          resolve();
        },
        error: (err) => {
          console.log(err);
          reject(err);
        },
      });
    });
  }

  getOrganizations() {
    this.organizationService.list().subscribe({
      next: (resOrganization: any) => {
        this.organizations = resOrganization;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getProcedures() {
    this.procedureService.getProcedures().subscribe((result: any[]) => {
      this.procedures = result.map(this.transformToFormData);
    });
  }

  getProceduresNearest() {
    this.loading = true;
    this.hasSearched = false;

    let params = new HttpParams();

    const position = this.latLong.join(',');
    params = params.set('position', position);
    params = params.set('radiusInMeters', this.distance);

    this.procedureService.listNearest(params).subscribe({
      next: (healthcareservices: Healthcareservice[]) => {
        this.procedures = healthcareservices;
        this.currentPage = 0;

        this.loading = false;
        this.hasSearched = true;
        if (this.search.length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (error) => {
        this.loading = false;
        this.hasSearched = true;
        this.procedures = [];
        console.log(error);
      },
    });

    this.procedureService.listNearestBySpecialists(params).subscribe({
      next: (specialists) => {
        this.specialistResults = specialists;
        if (this.search.length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (error) => {
        this.specialistResults = [];
        console.error('Erro ao buscar especialistas próximos:', error);
      },
    });
  }

  getLocations() {
    this.locationService.list().subscribe({
      next: (resLocation: any) => {
        const res = resLocation;
        this.locations = res.map((location: any) => this.transformLocation(location));
        if (this.search.trim().length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (err) => {
        console.log('erro', err);
      },
    });
  }

  getCity(organization_id: string) {
    const location = this.locations.find((location) => location.organization.organization_id == organization_id);
    if (!location) return null;
    return `${location?.city} / ${location.state}`;
  }

  getAddress(organization_id: string) {
    const location = this.locations.find((location) => location.organization.organization_id == organization_id);
    return location;
  }

  getAddressForProcedure(procedure: Healthcareservice) {
    const locationId = procedure.location?.location_id;
    if (locationId) {
      const exactLocation = this.locations.find((location) => location.location_id === locationId);
      if (exactLocation) return exactLocation;
    }
    return this.getAddress(procedure.organization.organization_id);
  }

  getHospitalName(procedure: procedureI) {
    if (procedure.organization) {
      return `<br>${procedure.organization.name}  `;
    }
    return '';
  }

  getOrganizationPhoto(organization_id: string): string {
    const organization = this.organizations.find((org) => org.organization_id === organization_id);
    const url = organization && organization.photo ? organization.photo : this.defaultOrganizationPhoto;
    return url;
  }

  openOrnigazationInfos(organization_id: string) {
    const organization = this.organizations.find((organization) => organization.organization_id === organization_id);
    const organizationLocation = this.locations.find((location) => location.organization.organization_id === organization_id);

    if (organization!.photo == '') {
      organization!.photo = this.defaultOrganizationPhoto;
    }

    const data = {
      param: {
        organization,
        organizationLocation,
      },
    };

    this.dialog.openComponentDialog(OrganizationInfoDialogComponent, data);
  }

  transformToFormData(healthcareserviceData: any): any {
    return {
      healthcareservice_id: healthcareserviceData.healthcareservice_id,
      name: healthcareserviceData.name,
      description: healthcareserviceData.description,
      organization: {
        name: healthcareserviceData.providedBy.display,
        organization_id: healthcareserviceData.providedBy.code,
      },
      extraDetails: healthcareserviceData.extraDetails,
      type: healthcareserviceData?.type[0]?.coding[0]?.display,
      price_low: parseInt(healthcareserviceData?.valueRange?.low?.value),
      price_high: parseInt(healthcareserviceData?.valueRange?.high?.value),
      status: healthcareserviceData.status,
      specialty: healthcareserviceData.specialty[0].coding[0].display,
      comment: healthcareserviceData.comments,
      appointmentRequired: healthcareserviceData.appointmentRequired,
    };
  }

  transformLocation(location: any) {
    const address = location?.address[0];
    const speciality = location?.type[0]?.coding[0];

    const newAddress: any = {};

    newAddress.location_id = location?.location_id;
    newAddress.name = location?.name;
    newAddress.alias = location?.alias;

    newAddress.organization = {
      name: location?.name,
      organization_id: location.organization_id,
    };
    newAddress.position = location.position;

    const cep = address.postalCode;

    newAddress.cep = cep;

    newAddress.country = address?.country;

    newAddress.state = address?.state;

    newAddress.city = address?.city;

    newAddress.district = address?.district;
    const extraInfos = address?.line;

    if (extraInfos) {
      newAddress.street = extraInfos[0] ?? '';
      newAddress.number = extraInfos[1] ?? '';
      newAddress.complement = extraInfos[2] ?? '';
    }

    const contactInfo = this.extractContactInfo(location?.telecom);

    if (contactInfo) {
      newAddress.phone = contactInfo.phone ?? '';
      newAddress.mobile = contactInfo.mobile ?? '';
      newAddress.mail = contactInfo.email ?? '';
    }

    if (speciality) {
      newAddress.speciality = speciality?.display;
    }

    newAddress.mapUrl = location.mapUrl;
    return newAddress;
  }

  extractContactInfo(telecom: telecom[]) {
    let email: string | null = null;
    let phone: string | null = null;
    let mobile: string | null = null;

    telecom.forEach((telecom) => {
      const { value, use, system } = telecom;
      if (system === 'email') {
        email = value;
      } else if (system === 'phone') {
        phone = value;
      } else if (system === 'mobile') {
        mobile = value;
      }
    });
    return { email, phone, mobile };
  }

  formatLabel(value: number): string {
    localStorage.setItem('distancePatient', value.toString());

    if (value >= 1000) {
      return Math.round(value / 1000) + 'km';
    }

    return `${value}`;
  }
}
